"use client";

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/utils";

const formSchema = z.object({
  name: z.string().min(1, "Name is required").max(255),
  add_on_type: z.enum(["food", "drink", "service", "equipment", "other"]),
  pricing_id: z.number().min(1, "Pricing selection is required"),
  is_on_road: z.boolean(),
});

type FormValues = z.infer<typeof formSchema>;

interface AddOn {
  id: number;
  venue_id: number;
  name: string;
  add_on_type: string;
  pricing_id: number;
  is_on_road?: boolean;
}

interface Pricing {
  id: number;
  price: number;
  vat_percentage: number;
}

interface AddOnFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  venueId: number;
  addOn?: AddOn | null;
  onSuccess: () => void;
}

export function AddOnFormDialog({
  open,
  onOpenChange,
  venueId,
  addOn,
  onSuccess,
}: AddOnFormDialogProps) {
  const [loading, setLoading] = useState(false);
  const [pricings, setPricings] = useState<Pricing[]>([]);
  const [loadingPricings, setLoadingPricings] = useState(true);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      add_on_type: "food",
      pricing_id: 0,
      is_on_road: false,
    },
  });

  useEffect(() => {
    if (open) {
      fetchPricings();
    }
  }, [open]);

  useEffect(() => {
    if (addOn) {
      form.reset({
        name: addOn.name,
        add_on_type: addOn.add_on_type as any,
        pricing_id: addOn.pricing_id,
        is_on_road: addOn.is_on_road ?? false,
      });
    } else {
      form.reset({
        name: "",
        add_on_type: "food",
        pricing_id: 0,
        is_on_road: false,
      });
    }
  }, [addOn, form]);

  const fetchPricings = async () => {
    try {
      const response = await fetch("/api/pricings");
      if (!response.ok) throw new Error("Failed to fetch pricings");
      const data = await response.json();
      setPricings(data);
    } catch (error) {
      console.error("Error fetching pricings:", error);
      toast.error("Failed to load pricing options");
    } finally {
      setLoadingPricings(false);
    }
  };

  const onSubmit = async (values: FormValues) => {
    setLoading(true);
    try {
      const url = addOn
        ? `/api/venues/${venueId}/add-ons/${addOn.id}`
        : `/api/venues/${venueId}/add-ons`;
      const method = addOn ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(values),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save add-on");
      }

      toast.success(`Add-on ${addOn ? "updated" : "created"} successfully`);

      onSuccess();
    } catch (error: any) {
      toast.error(error.message || "Failed to save add-on");
    } finally {
      setLoading(false);
    }
  };

  const addOnTypes = [
    { value: "food", label: "Food" },
    { value: "drink", label: "Drink" },
    { value: "service", label: "Service" },
    { value: "equipment", label: "Equipment" },
    { value: "other", label: "Other" },
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader className="pb-4">
          <DialogTitle className="text-xl font-semibold">
            {addOn ? "Edit Add-On" : "Add New Add-on"}
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            {addOn
              ? "Update the add-on details below."
              : "Enter the details for the new add-on. You can configure additional settings after creation."}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="space-y-6 py-4"
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Add-on Name *
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder="e.g., Welcome Drink, Photo Package"
                      className="w-full"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription className="text-xs text-gray-500">
                    You can add translations and other details in the edit page
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="add_on_type"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Category *
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select add-on category" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {addOnTypes.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription className="text-xs text-gray-500">
                    Choose the category that best fits this add-on
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="pricing_id"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <FormLabel className="text-sm font-medium text-gray-700">
                    Price *
                  </FormLabel>
                  <Select
                    onValueChange={(value) => field.onChange(parseInt(value))}
                    value={field.value?.toString()}
                    disabled={loadingPricings}
                  >
                    <FormControl>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select pricing" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {pricings.length === 0 && !loadingPricings && (
                        <div className="px-2 py-4 text-center text-sm text-gray-500">
                          No pricing options available. Please create pricing
                          options first.
                        </div>
                      )}
                      {pricings.map((pricing) => (
                        <SelectItem
                          key={pricing.id}
                          value={pricing.id.toString()}
                        >
                          {formatCurrency(pricing.price)} (incl.{" "}
                          {pricing.vat_percentage}% VAT)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormDescription className="text-xs text-gray-500">
                    Select the pricing tier for this add-on
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="is_on_road"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start gap-3 space-y-0 rounded-md border p-4">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel className="text-sm font-medium text-gray-700">
                      Road show add-on
                    </FormLabel>
                    <FormDescription className="text-xs text-gray-500">
                      When enabled, this add-on is only offered on road-show
                      bookings (not on-site bookings).
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            <DialogFooter className="pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={loading}
                className="mr-2"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={
                  loading || !form.watch("name") || !form.watch("pricing_id")
                }
                className="bg-purple-600 hover:bg-purple-700"
              >
                {loading ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                {addOn ? "Update Add-on" : "Create Add-on"}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
